import { Key, useCallback, useEffect, useState } from 'react'
import { ReactState } from '../../React/Common/Contexts'

export enum SELECTION_STATE {
    ALL = 'all',
    NONE = 'none',
    PARTIAL = 'partial'
}

export type SelectionState = SELECTION_STATE.ALL | SELECTION_STATE.PARTIAL | SELECTION_STATE.NONE

export type useSelectionReturn<T> = [
    number,
    SelectionState,
    (key: Key) => boolean,
    (key: Key, data: T[]) => void,
    (keys: Key[], data: T[]) => void,
    (data: T[]) => void,
    () => void
]

interface SelectionData {
    state: SelectionState
    selected: Record<Key, boolean>
    count: number
}

const emptySelectionData: () => SelectionData = (): SelectionData => ({
    state: SELECTION_STATE.NONE,
    selected: {},
    count: 0
})

type useSelectionHook = <
    T extends {
        key: Key
        [key: string]: Key
    }
>(
    data: T[],
    selectionEnabled: boolean,
    preserveSelectionOnDataChange?: boolean
) => useSelectionReturn<T>

export const useSelection: useSelectionHook = <T extends { key: Key; [key: string]: Key }>(
    data: T[],
    selectionEnabled: boolean,
    preserveSelectionOnDataChange: boolean = false
): useSelectionReturn<T> => {
    const [selection, setSelection]: ReactState<SelectionData> = useState<SelectionData>(emptySelectionData())

    useEffect((): void => {
        if (!selectionEnabled) {
            setSelection(emptySelectionData())

            return
        }

        if (!preserveSelectionOnDataChange) {
            setSelection(emptySelectionData())

            return
        }

        setSelection((currentSelection: SelectionData): SelectionData => {
            const availableKeys: Set<string> = new Set(data.map((item: T): string => String(item.key)))

            const selected: Record<Key, boolean> = Object.keys(currentSelection.selected).reduce(
                (accumulator: Record<Key, boolean>, key: string): Record<Key, boolean> => {
                    if (availableKeys.has(String(key))) {
                        accumulator[key] = true
                    }

                    return accumulator
                },
                {}
            )

            const count: number = Object.keys(selected).length

            let state: SelectionState = SELECTION_STATE.PARTIAL

            if (count === 0) {
                state = SELECTION_STATE.NONE
            } else if (count === data.length) {
                state = SELECTION_STATE.ALL
            }

            return {
                selected,
                count,
                state
            }
        })
    }, [data, selectionEnabled, preserveSelectionOnDataChange])

    const isSelected: (key: Key) => boolean = useCallback(
        (key: Key): boolean => selection.selected[key] ?? false,
        [selection]
    )

    const toggleOne: (key: Key, data: T[]) => void = useCallback(
        (key: Key, currentData: T[]): void => {
            const selected: Record<Key, boolean> = { ...selection.selected }

            if (selected[key]) {
                delete selected[key]
            } else {
                selected[key] = true
            }

            const count: number = Object.keys(selected).length

            let state: SelectionState = SELECTION_STATE.NONE

            if (count > 0) {
                state = count === currentData.length ? SELECTION_STATE.ALL : SELECTION_STATE.PARTIAL
            }

            setSelection({ state, selected, count })
        },
        [selection]
    )

    const toggleMultiple: (keys: Key[], data: T[]) => void = useCallback(
        (keys: Key[], currentData: T[]): void => {
            const selected: Record<Key, boolean> = { ...selection.selected }
            keys.forEach((key: Key): void => {
                if (selected[key]) {
                    delete selected[key]
                } else {
                    selected[key] = true
                }
            })
            const count: number = Object.keys(selected).length

            let state: SelectionState = SELECTION_STATE.NONE

            if (count > 0) {
                state = count === currentData.length ? SELECTION_STATE.ALL : SELECTION_STATE.PARTIAL
            }

            setSelection({ state, selected, count })
        },
        [selection]
    )

    const toggleAll: (data: T[]) => void = useCallback(
        (currentData: T[]): void => {
            let selected: Record<Key, boolean> = {}
            let count: number = 0
            let state: SelectionState = SELECTION_STATE.NONE

            if (selection.state !== SELECTION_STATE.ALL) {
                state = SELECTION_STATE.ALL
                selected = currentData.reduce(
                    (acc: Record<Key, boolean>, n: T): Record<Key, boolean> => {
                        acc[n.key] = true
                        return acc
                    },
                    {} as Record<Key, boolean>
                )
                count = currentData.length
            }

            setSelection({ state, selected, count })
        },
        [selection]
    )

    const reset: () => void = useCallback((): void => {
        setSelection(emptySelectionData())
    }, [])

    return [selection.count, selection.state, isSelected, toggleOne, toggleMultiple, toggleAll, reset]
}
